import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, AlertTriangle, ArrowLeft, Download } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const DepositPage = () => {
    const [searchParams] = useSearchParams();
    const initialTokenId = searchParams.get('tokenId');

    const [tokens, setTokens] = useState([]);
    const [selectedToken, setSelectedToken] = useState(null);
    const [networks, setNetworks] = useState([]);
    const [selectedNetwork, setSelectedNetwork] = useState(null);
    const [depositAddress, setDepositAddress] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTokens();
    }, []);

    const fetchTokens = async () => {
        try {
            const res = await api.get('/wallet/tokens');
            const supportedTokens = res.data.tokens || [];
            setTokens(supportedTokens);

            if (initialTokenId) {
                const preselected = supportedTokens.find(t => String(t.id) === initialTokenId);
                if (preselected) handleTokenSelect(preselected);
            } else if (supportedTokens.length > 0) {
                handleTokenSelect(supportedTokens[0]);
            }
        } catch (error) {
            toast.error('Failed to load tokens');
        } finally {
            setLoading(false);
        }
    };

    const handleTokenSelect = async (token) => {
        setSelectedToken(token);
        setSelectedNetwork(null);
        setDepositAddress('');
        setNetworks([]);

        if (!token.depositEnabled) return;

        try {
            const res = await api.get(`/wallet/deposit/${token.id}`);
            const { addresses } = res.data.depositInfo;

            setNetworks(addresses || []);
            if (addresses && addresses.length > 0) {
                setSelectedNetwork(addresses[0]);
                setDepositAddress(addresses[0].address);
            }
        } catch (error) {
            console.error('Failed to fetch deposit address:', error);
            toast.error('Failed to fetch deposit info');
        }
    };

    const handleNetworkChange = (networkId) => {
        const network = networks.find(n => n.networkId === networkId);
        if (network) {
            setSelectedNetwork(network);
            setDepositAddress(network.address);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(depositAddress);
        toast.success('Address copied');
    };

    return (
        <div className="bg-[#0b0e11] min-h-screen text-gray-100 font-sans selection:bg-blue-500/30">
            <Navbar />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-8">
                    <Link to="/wallet" className="text-gray-400 hover:text-white flex items-center gap-2 mb-4 transition">
                        <ArrowLeft size={20} /> Back to Wallet
                    </Link>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <div className="bg-blue-500/10 p-2 rounded-xl text-blue-500">
                            <Download size={24} />
                        </div>
                        Deposit Crypto
                    </h1>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left Column: Selection */}
                    <div className="space-y-6">
                        <div className="bg-[#181a20] p-6 rounded-2xl border border-gray-800">
                            <label className="block text-gray-400 mb-2 font-medium">Select Coin</label>
                            <select
                                value={selectedToken?.id || ''}
                                onChange={(e) => {
                                    const token = tokens.find(t => String(t.id) === e.target.value);
                                    handleTokenSelect(token);
                                }}
                                className="w-full bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none transition"
                            >
                                <option value="">Select Coin</option>
                                {tokens.map(t => (
                                    <option key={t.id} value={t.id}>{t.symbol} - {t.name}</option>
                                ))}
                            </select>
                        </div>

                        {selectedToken && (
                            <div className="bg-[#181a20] p-6 rounded-2xl border border-gray-800">
                                <label className="block text-gray-400 mb-2 font-medium">Select Network</label>
                                <div className="flex flex-wrap gap-2">
                                    {networks.length > 0 ? networks.map(net => (
                                        <button
                                            key={net.networkId}
                                            onClick={() => handleNetworkChange(net.networkId)}
                                            className={`px-4 py-2 rounded-xl text-sm font-bold border transition ${selectedNetwork?.networkId === net.networkId
                                                    ? 'bg-blue-600 border-blue-600 text-white'
                                                    : 'bg-[#0b0e11] border-gray-700 text-gray-400 hover:border-gray-500'
                                                }`}
                                        >
                                            {net.networkSymbol}
                                        </button>
                                    )) : (
                                        <span className="text-gray-500 text-sm">No networks available</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column: Address & QR */}
                    <div className="bg-[#181a20] p-8 rounded-3xl border border-gray-800 flex flex-col items-center justify-center text-center min-h-[400px]">
                        {loading ? (
                            <div className="animate-pulse flex flex-col items-center">
                                <div className="w-48 h-48 bg-gray-800 rounded-xl mb-4"></div>
                                <div className="h-4 bg-gray-800 w-64 rounded"></div>
                            </div>
                        ) : !selectedToken ? (
                            <div className="text-gray-500">
                                <Download size={48} className="mx-auto mb-4 opacity-50" />
                                <p>Select a coin to view deposit address</p>
                            </div>
                        ) : !selectedToken.depositEnabled ? (
                            <div className="text-yellow-500">
                                <AlertTriangle size={48} className="mx-auto mb-4" />
                                <p>Deposits are currently disabled for this asset.</p>
                            </div>
                        ) : depositAddress ? (
                            <>
                                <div className="bg-white p-4 rounded-xl mb-6 shadow-lg">
                                    <QRCodeSVG value={depositAddress} size={180} />
                                </div>

                                <div className="w-full">
                                    <p className="text-gray-400 text-sm mb-2">Deposit Address ({selectedNetwork?.networkSymbol})</p>
                                    <div className="flex items-center gap-2 bg-[#0b0e11] p-3 rounded-xl border border-gray-700/50 group hover:border-blue-500/50 transition">
                                        <code className="flex-1 text-sm text-gray-300 break-all font-mono">{depositAddress}</code>
                                        <button
                                            onClick={copyToClipboard}
                                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition"
                                        >
                                            <Copy size={18} />
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-6 bg-blue-500/10 p-4 rounded-xl border border-blue-500/20 text-left w-full">
                                    <div className="flex gap-3">
                                        <AlertTriangle className="text-blue-400 flex-shrink-0" size={20} />
                                        <ul className="text-sm text-blue-200/80 space-y-1 list-disc pl-4">
                                            <li>Send only <strong>{selectedToken.symbol}</strong> to this address.</li>
                                            <li>Make sure the network matches <strong>{selectedNetwork?.networkSymbol}</strong>.</li>
                                            <li>Deposits usually take 10-30 minutes to confirm.</li>
                                        </ul>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center text-gray-500">
                                <p>Address not available. Please contact support.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DepositPage;
